import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { sameTagIds } from "../lib/taskUpdatePayload";
import type { Task } from "../types";

/**
 * The task dialog's tag selection, plus the bookkeeping that tells a pick the
 * user has not saved yet apart from tags arriving from the server.
 *
 * Tags are the one part of an edited task that lives outside `editingTask`:
 * they are written by their own RPC, not by the task-row update. That makes
 * them invisible to the snapshot comparison `useTaskEditing` uses for
 * everything else, so they need a mirror and a baseline of their own — the
 * baseline is to the tags what `originalEditingTask` is to the task.
 *
 * Both halves earned their place by breaking:
 *
 * - Without `hasPendingChange`, the refetch a save triggers — which arrives
 *   before the tags are written — was adopted over a tag the user had just
 *   picked, and the next save persisted that reversal.
 * - Without the identity care in `adopt`, every refetch handed the dialog's
 *   auto-save a new array holding the same ids. Read as an edit, it scheduled
 *   a save, whose refetch scheduled the next one — the dialog wrote to the
 *   database for as long as it stayed open.
 */
export interface TagSelectionSync {
  /** True while the selection differs from what the server last showed us. */
  hasPendingChange: () => boolean;
  /** Take the task's tags as the new truth, selection and baseline together. */
  adopt: (task: Task) => void;
  /** Move the baseline after a write lands, so the selection reads as saved. */
  markSaved: (savedTagIds: string[]) => void;
  /** Clear both on close, so reopening isn't misread as a pending edit. */
  reset: () => void;
}

export function useEditingTaskTags(): {
  tagIds: string[];
  setTagIds: React.Dispatch<React.SetStateAction<string[]>>;
  sync: TagSelectionSync;
} {
  const [tagIds, setTagIds] = useState<string[]>([]);
  // Mirrored in a layout effect rather than read as a dependency: the sync
  // effect that consumes this must not re-run on every selection change.
  const selectedRef = useRef<string[]>([]);
  const baselineRef = useRef<string[]>([]);
  useLayoutEffect(() => {
    selectedRef.current = tagIds;
  });

  // Stable for the life of the dialog — it closes over refs and a setter only.
  // That matters: the effect that calls `adopt` lists this in its dependencies,
  // and a fresh object per render would re-run it on every render.
  const sync = useMemo<TagSelectionSync>(
    () => ({
      hasPendingChange: () =>
        !sameTagIds(selectedRef.current, baselineRef.current),
      adopt: (task) => {
        const fresh = task.tags?.map((t) => t.id) || [];
        baselineRef.current = fresh;
        // Held at the old array when the ids are unchanged — see the note above
        // on what a fresh array costs.
        setTagIds((prev) => (sameTagIds(prev, fresh) ? prev : fresh));
      },
      markSaved: (savedTagIds) => {
        baselineRef.current = savedTagIds;
      },
      reset: () => {
        baselineRef.current = [];
        setTagIds([]);
      },
    }),
    [],
  );

  return { tagIds, setTagIds, sync };
}
